import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Download, Plus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

import { AdminOrgPicker } from '@apps/web/components/admin/admin_org_picker';
import { CaregiverFormModal } from '@apps/web/components/admin/caregiver_form_modal';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import { SearchInput } from '@apps/web/components/ui/search_input';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { useDebounce } from '@apps/web/hooks/use_debounce';
import { toast } from '@apps/web/lib/app_notifications';
import { modeStore } from '@apps/web/lib/mode_store';
import { getNavigableElementProps } from '@apps/web/lib/navigation_utils';
import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import { useAdminCaregivers } from '@apps/web/swr_data_fetching/admin_org_record_queries';
import { useAllAdminOrganizations } from '@apps/web/swr_data_fetching/admin_organization_queries';
import { type AdminOrgCaregiverRecordListResponse } from '@libraries/typescript/api_client';

const caregiversSearchSchema = z.object({
    organizationId: z.string().optional(),
    search: z.string().optional(),
});

export const Route = createFileRoute('/_app/admin/caregivers/')({
    head: () => ({
        meta: [
            {
                title: 'Caregivers | Phoebe Admin',
            },
        ],
    }),
    validateSearch: caregiversSearchSchema,
    component: AdminCaregiversPage,
});

function AdminCaregiversPage() {
    const router = useRouter();
    const navigate = Route.useNavigate();
    const { organizationId, search } = Route.useSearch();
    const searchQuery = search ?? '';
    const [searchDraft, setSearchDraft] = useState(searchQuery);
    const debouncedSearch = useDebounce(searchDraft, 300);
    // Mirrors the client page behavior: URL changes own the draft unless the
    // current draft came from direct user typing.
    const searchDraftEditedRef = useRef(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    useEffect(() => {
        searchDraftEditedRef.current = false;
        setSearchDraft(searchQuery);
    }, [searchQuery]);

    useEffect(() => {
        if (!searchDraftEditedRef.current) {
            return;
        }
        if (debouncedSearch === searchQuery) {
            return;
        }

        void navigate({
            search: (current) => ({
                ...current,
                search: debouncedSearch || undefined,
            }),
            replace: true,
        });
    }, [debouncedSearch, navigate, searchQuery]);

    const {
        data: caregiversResponse,
        isLoading,
        mutate: mutateCaregivers,
    } = useAdminCaregivers({
        page: 1,
        per_page: 100,
        ...(organizationId && { organization_id: organizationId }),
        ...(searchQuery && { search: searchQuery }),
    }) as {
        data: AdminOrgCaregiverRecordListResponse | undefined;
        isLoading: boolean;
        mutate: () => void;
    };

    const { organizations } = useAllAdminOrganizations({
        autoLoadPages: true,
        orgDataOnly: true,
    });

    const caregivers = caregiversResponse?.caregivers ?? [];
    const organizationInternalNamesById = useMemo(
        () =>
            new Map(
                (organizations ?? []).map((organization) => [
                    organization.id,
                    organization.internal_name,
                ]),
            ),
        [organizations],
    );
    const getCaregiverNavigation = (caregiverId: string) => {
        const locationOptions = {
            to: '/admin/caregivers/$caregiver-id',
            params: { 'caregiver-id': caregiverId },
            search: { organizationId, search: searchDraft || undefined },
        } as const;
        return {
            href: router.buildLocation(locationOptions).href,
            navigate: () => {
                void router.navigate(locationOptions);
            },
        };
    };

    const handleExportCsv = async () => {
        if (!organizationId) {
            return;
        }
        try {
            const runtimeConfig = getRuntimeConfig();
            const mode = modeStore.getMode();
            const response = await fetch(
                `${runtimeConfig.apiUrl}/admin/caregivers/csv_export?organization_id=${organizationId}`,
                {
                    credentials: 'include',
                    headers: {
                        ...(mode ? { 'X-Mode': mode } : {}),
                    },
                },
            );
            if (!response.ok) {
                throw new Error(`Export failed with ${response.status}`);
            }
            const blob = await response.blob();
            const orgName =
                organizationInternalNamesById.get(organizationId) ?? 'export';
            const safeName = orgName
                .replace(/[^a-zA-Z0-9 \-_]/g, '')
                .trim()
                .replace(/ /g, '-');
            const filename = `caregivers-${safeName}.csv`;
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = filename;
            anchor.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error('CSV export failed', {
                description: 'Please try again.',
                persistNotification: false,
            });
        }
    };

    return (
        <div className="flex h-full flex-col">
            <div className="border-border border-b p-4">
                <div className="mb-4 flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Caregivers</h2>
                    <div className="flex items-center gap-4">
                        <div className="text-muted-foreground text-sm">
                            {caregiversResponse?.total ?? 0}{' '}
                            {organizationId ? 'in organization' : 'total'}
                        </div>
                        {organizationId && (
                            <Button
                                onClick={handleExportCsv}
                                variant="outline"
                                size="sm"
                                className="gap-2"
                            >
                                <Download className="h-4 w-4" />
                                Export CSV
                            </Button>
                        )}
                        <Button
                            onClick={() => setIsCreateModalOpen(true)}
                            size="sm"
                            className="gap-2"
                        >
                            <Plus className="h-4 w-4" />
                            Create
                        </Button>
                    </div>
                </div>
                <div className="space-y-3">
                    <SearchInput
                        placeholder="Search caregivers…"
                        value={searchDraft}
                        onValueChange={(value) => {
                            searchDraftEditedRef.current = true;
                            setSearchDraft(value);
                        }}
                    />
                    <div className="flex items-center gap-2">
                        <span className="text-muted-foreground text-sm font-medium">
                            Organization:
                        </span>
                        <AdminOrgPicker
                            value={organizationId}
                            onValueChange={(id) => {
                                void router.navigate({
                                    to: '/admin/caregivers',
                                    search: (prev) => ({
                                        ...prev,
                                        organizationId: id,
                                    }),
                                });
                            }}
                        />
                    </div>
                </div>
            </div>
            <ScrollArea className="flex-1 overflow-auto">
                {isLoading ? (
                    <div className="flex h-32 items-center justify-center">
                        <div className="text-muted-foreground flex items-center gap-2">
                            <Spinner />
                            <span>Loading caregivers…</span>
                        </div>
                    </div>
                ) : caregivers?.length === 0 ? (
                    <div className="flex h-32 items-center justify-center">
                        <div className="text-muted-foreground text-sm">
                            {searchQuery
                                ? 'No caregivers found'
                                : 'No caregivers yet'}
                        </div>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Organization</TableHead>
                                <TableHead>
                                    Organization Internal Name
                                </TableHead>
                                <TableHead>Contacts</TableHead>
                                <TableHead>Roles</TableHead>
                                <TableHead>Mode</TableHead>
                                <TableHead>Status</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {caregivers.map((caregiver) => (
                                <TableRow
                                    key={caregiver.id}
                                    className="cursor-pointer"
                                    {...getNavigableElementProps(
                                        getCaregiverNavigation(caregiver.id),
                                    )}
                                >
                                    <TableCell className="font-medium">
                                        {caregiver.name}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {caregiver.organization_name}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {organizationInternalNamesById.get(
                                            caregiver.organization_id,
                                        ) ?? '-'}
                                    </TableCell>
                                    <TableCell>
                                        {caregiver.contact_count || (
                                            <span className="text-muted-foreground">
                                                -
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex gap-1">
                                            {caregiver.roles?.map((role) => (
                                                <Badge
                                                    key={role}
                                                    variant="outline"
                                                    className="capitalize"
                                                >
                                                    {role}
                                                </Badge>
                                            ))}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline">
                                            {caregiver.mode === 'sandbox'
                                                ? 'Sandbox'
                                                : 'Live'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <Badge
                                            variant={
                                                caregiver.is_active
                                                    ? 'default'
                                                    : 'secondary'
                                            }
                                        >
                                            {caregiver.is_active
                                                ? 'Active'
                                                : 'Inactive'}
                                        </Badge>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </ScrollArea>
            <CaregiverFormModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                mode="create"
                onSaveSuccess={() => mutateCaregivers()}
            />
        </div>
    );
}
